import {
  THEME_COLOR,
  DARK_MODE,
  GRAY_SCALE,
  BORDER,
  BODY_BACKGROUND,
    BORDER_LIGHT,
    BORDER_RADIUS,
    FONT_SIZES,
    FONT_WEIGHT,
} from "constants/ThemeConstant";
import {} from "@ant-design/colors";

export function rgba(hex, opacity = 1) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error("Invalid Hex");
  }

  const decimal = parseInt(hex.substring(1), 16);

  const r = (decimal >> 16) & 255;
  const g = (decimal >> 8) & 255;
  const b = decimal & 255;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const baseTheme = {
  borderRadius: BORDER_RADIUS.BASE,
  borderRadiusSM: BORDER_RADIUS.SM,
  borderRadiusMD: BORDER_RADIUS.MD,
  borderRadiusPill: BORDER_RADIUS.PILL,
  colorPrimary: THEME_COLOR.PURPLE,
  colorPrimaryHover: "#581BC9",
  colorSuccess: THEME_COLOR.CYAN,
  colorWarning: THEME_COLOR.GOLD,
  colorError: "#EE6352",
  colorInfo: THEME_COLOR.BLUE,
  colorText: GRAY_SCALE.GRAY,
  colorTextLightSolid: GRAY_SCALE.WHITE,
  colorBorder: BORDER.BASE_COLOR,
  colorBorderLight: BORDER_LIGHT,
  bodyBg: BODY_BACKGROUND,
  controlHeight: 40,
  controlHeightLG: 48,
  controlHeightSM: 36,
  fontFamily: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
  fontFamilyHeading: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
  fontFamilyHero: "'DM Serif Text', serif",
  fontSizeHeading2: 22,
  fontSizeHeading4: 17,
  fontSizeButton: FONT_SIZES.BUTTON,
  fontSizeHero: FONT_SIZES.HERO,
};

const getBaseComponentThemeConfig = (
  baseTheme,
  {
    Button = {},
    Menu = {},
    Card = {},
    Table = {},
    Select = {},
    DatePicker = {},
    Layout = {},
    Input = {},
    Dropdown = {},
    Calendar = {},
  } = {},
) => {
  return {
    Button: {
      ...Button,
      // 42px app-wide (was 48) — the platform's button rhythm; matches the
      // redeem brand buttons and the compact form controls
      controlHeight: 42,
      borderRadius: BORDER_RADIUS.PILL,
      borderRadiusLG: BORDER_RADIUS.PILL,
      borderRadiusSM: BORDER_RADIUS.PILL,
      fontSize: FONT_SIZES.BUTTON,
      fontWeightStrong: FONT_WEIGHT.BOLD,
      primaryColor: baseTheme.colorPrimary,
      colorPrimaryHover: baseTheme.colorPrimaryHover,
      primaryShadow: 'none',
      colorBgContainerDisabled: rgba(baseTheme.colorPrimary, 0.3),
      borderColorDisabled: 'transparent',
    },
    Dropdown: {
      controlPaddingHorizontal: 20,
      controlHeight: 37,
      borderRadiusLG: 10,
      paddingXXS: "4px 0px",
      ...Dropdown,
    },
    Calendar: {
      ...Calendar,
    },
    Checkbox: {
      lineWidth: 2,
      borderRadiusSM: BORDER_RADIUS.SM,
      colorBorder: BORDER_LIGHT,
      colorPrimary: baseTheme.colorPrimary,
      colorPrimaryHover: baseTheme.colorPrimary,
      controlInteractiveSize: 20,
    },
    Card: {
      colorTextHeading: GRAY_SCALE.GRAY_DARK,
      paddingLG: 20,
      ...Card,
    },
    Layout: {
      bodyBg: BODY_BACKGROUND,
      headerBg: GRAY_SCALE.WHITE,
      ...Layout,
    },
    Breadcrumb: {
      colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
      colorText: baseTheme.colorPrimary,
      colorBgTextHover: "transparent",
    },
    Menu: {
      itemBg: "transparent",
      colorActiveBarHeight: 0,
      activeBarWidth: 3,
      itemSelectedColorHorizontal: baseTheme.colorPrimary,
      itemHoverColor: baseTheme.colorPrimary,
      itemSelectedColor: baseTheme.colorPrimary,
      itemBgSelected: rgba(baseTheme.colorPrimary, 0.1),
      itemBgHover: "transparent",
      radiusItem: 0,
      subMenuItemBg: "transparent",
      itemMarginInline: 0,
      controlHeightLG: 40,
      controlHeightSM: 22,
      ...Menu,
    },
    Input: {
      controlHeight: 48,
      borderRadius: BORDER_RADIUS.MD,
      fontSize: FONT_SIZES.LG,
      colorBorder: BORDER_LIGHT,
      ...Input,
    },
    Pagination: {
      paginationItemSize: 30,
      borderRadius: "50%",
      itemActiveBg: baseTheme.colorPrimary,
      itemSize: "36px",
      lineHeight: "34px",
      colorPrimary: GRAY_SCALE.WHITE,
      colorPrimaryHover: GRAY_SCALE.WHITE,
    },
    Steps: {
      wireframe: true,
      controlHeight: 32,
      waitIconColor: GRAY_SCALE.GRAY_LIGHT,
    },
    DatePicker: {
      controlHeight: 40,
      controlHeightSM: 26,
      borderRadiusSM: 6,
      lineWidthBold: 0,
      ...DatePicker,
    },
    Radio: {
      fontSizeLG: 18,
    },
    Slider: {
      colorPrimaryBorder: rgba(baseTheme.colorPrimary, 0.8),
      colorPrimaryBorderHover: baseTheme.colorPrimary,
    },
    Select: {
      paddingXXS: "4px 0px",
      controlHeight: 40,
      controlHeightSM: 30,
      controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
      ...Select,
    },
    TreeSelect: {
      controlHeightSM: 24,
    },
    Tooltip: {
      colorBgDefault: rgba(GRAY_SCALE.DARK, 0.75),
      controlHeight: 32,
    },
    Timeline: {
      lineType: "dashed",
    },
    Tag: {
      lineHeight: 2.1,
    },
    Table: {
      colorText: GRAY_SCALE.GRAY,
      tableHeaderBg: "transparent",
      ...Table,
    },
  };
};

export const getLightTheme = (primaryColor) => {
  const hoverColorConfig = {};
  if (primaryColor !== baseTheme.colorPrimary) {
    hoverColorConfig.colorPrimaryHover = `${primaryColor}e6`;
  }
  return {
    token: {
      ...baseTheme,
      ...hoverColorConfig,
      colorPrimary: primaryColor,
      colorLink: baseTheme.colorPrimary,
      colorLinkHover: baseTheme.colorPrimaryHover,
      colorTextBase: GRAY_SCALE.GRAY,
    },
    components: getBaseComponentThemeConfig({
      ...baseTheme,
      colorPrimary: primaryColor,
    }),
  };
};

export const darkTheme = {
  token: {
    ...baseTheme,
    colorTextBase: DARK_MODE.TEXT_COLOR,
    colorBgBase: DARK_MODE.BG_COLOR,
    colorBorder: DARK_MODE.BORDER_BASE_COLOR,
    colorText: DARK_MODE.TEXT_COLOR,
  },
  components: getBaseComponentThemeConfig(baseTheme, {
    Button: {
      controlOutline: "transparent",
      colorBorder: DARK_MODE.BORDER_BASE_COLOR,
      colorText: DARK_MODE.TEXT_COLOR,
    },
    Card: {
      colorTextHeading: DARK_MODE.HEADING_COLOR,
    },
    Layout: {
      bodyBg: "#1b2531",
      headerBg: DARK_MODE.BG_COLOR,
    },
    Menu: {
      itemBgSelected: "transparent",
      itemSelectedColorHorizontal: GRAY_SCALE.WHITE,
      itemHoverColor: GRAY_SCALE.WHITE,
      itemSelectedColor: GRAY_SCALE.WHITE,
      colorItemText: DARK_MODE.TEXT_COLOR,
      activeBarWidth: 0,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Dropdown: {
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Calendar: {
      controlItemBgActive: "#303a4e",
    },
    Select: {
      controlOutline: "transparent",
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Input: {
      controlOutline: "transparent",
    },
    DatePicker: {
      controlOutline: "transparent",
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
    },
    Table: {
      colorText: DARK_MODE.TEXT_COLOR,
      colorTextHeading: DARK_MODE.HEADING_COLOR,
    },
  }),
};
